import React, { useMemo, useState } from 'react'

import { Popover } from '@headlessui/react'
import {
  HandThumbUpIcon as HandThumbUpIconSolid,
  PencilSquareIcon,
} from '@heroicons/react/20/solid'
import { CheckBadgeIcon, EyeIcon, HandThumbUpIcon, TrashIcon } from '@heroicons/react/24/outline'
import { formatDistanceToNow } from 'date-fns'
import _get from 'lodash/get'

import { ConfirmDeleteModal, EditNoteModal, useStreamingNotes } from './components'
import { DownloadFile } from './components/DownloadFile'
import { capitalizeFirstLastNames, processMentionedEntity } from './components/utils'
import {
  setAanReasons,
  setClosedLossDrawerOpen,
  setClosedLossDrawerReason,
  setClosedLossDrawerSubReasonOtherText,
  setClosedLossDrawerSubReasons,
  setClosedLossDrawerType,
} from '@/client/generalSlice'
import { useAppDispatch } from '@/client/hooks'
import Avatar from '@/components/WorkQueuesContent/components/avatar'
import { dealTeams } from '@/constants/dealTeams'
import { subLossReasonsOptions } from '@/features/ClosedLossDrawer/options'
import { getColor } from '@/key-components/utils/KeyColors'
import { useGetDealTeamsByApplicationQuery } from '@/services/application'
import {
  useDeleteStreamingNoteReactionMutation,
  useGetStreamingNotesByApplicationQuery,
  useMarkStreamingNoteAsReadMutation,
  useReactToStreamingNoteMutation,
  useUpdateStreamingNoteMutation,
} from '@/services/streamingNote'
import { StreamingNote, UserData } from '@/types/unlock'
import { systemUsers } from '@/utils/user'
import { getUserGroup } from '@/utils/userGroups'

interface Bubble {
  message: StreamingNote
  isReply?: boolean
  setShowComposer?: (showComposer: boolean) => void
  showComposer?: () => void
  isRepliesOpen?: boolean
  setIsRepliesOpen?: React.Dispatch<React.SetStateAction<boolean>>
  hasReplies?: boolean
  isLastIndex?: boolean
}

export function Bubble({
  message,
  isReply = false,
  setShowComposer,
  showComposer,
  isRepliesOpen,
  setIsRepliesOpen,
  hasReplies,
  isLastIndex,
}: Bubble) {
  const userFullName = _get(message, 'user.full_name')
  const userName = _get(message, 'user.user.username', 'Unknown User')
  const displayUserName = capitalizeFirstLastNames(userFullName || userName)
  const [showFullDate, setShowFullDate] = useState(false)
  const {
    DELETED_MESSAGE_TEXT,
    isNoteDeletable,
    isNoteDeleted,
    isMessageFromCurrentUser,
    isMessageSeenByCurrentUser,
    getUserById,
    loggedUser,
  } = useStreamingNotes()
  const dispatch = useAppDispatch()
  const [isConfirmDeleteOpen, setIsConfirmDeleteOpen] = useState(false)
  const [markStreamingNoteAsRead] = useMarkStreamingNoteAsReadMutation()
  const [seen, setSeen] = useState(false)
  const [isEditNoteOpen, setIsEditNoteOpen] = useState(false)
  const [updateStreamingNote] = useUpdateStreamingNoteMutation()
  const [reactToStreamingNote, { isLoading: isLoadingSNReaction }] =
    useReactToStreamingNoteMutation()
  const [deleteStreamingNoteReaction, { isLoading: isDeletingSNReaction }] =
    useDeleteStreamingNoteReactionMutation()
  const { refetch } = useGetStreamingNotesByApplicationQuery({
    page: 1,
    only_parent: true,
    application_id: message.application,
  })

  const { data: applicationDealTeams } = useGetDealTeamsByApplicationQuery(message.application, {
    skip: !message.application,
  })

  const assignedHEO = applicationDealTeams?.results?.find((dealTeam) => dealTeam.type === 'sales')

  const isAssignedHEO =
    !!assignedHEO &&
    message?.tagged_users?.find(
      (user: UserData) =>
        user?.id === loggedUser?.data?.id || assignedHEO?.user === loggedUser?.data?.id
    )

  const avatarColor = useMemo(() => {
    const messageOwner = message?.user?.user

    if (messageOwner?.groups?.length === 0) {
      return getColor('black')
    }

    if (systemUsers.includes(messageOwner?.email)) {
      return getColor('blue', 'l4')
    }

    const userGroup = messageOwner?.groups[0]?.name

    if (applicationDealTeams) {
      const userDealTeam = applicationDealTeams.results.filter(
        (dt) => dt.user === message?.user?.user?.id
      )

      if (userDealTeam.length > 0) {
        return dealTeams[userDealTeam[0].type]?.color
      }
    }

    return dealTeams[userGroup]?.color ?? getUserGroup(messageOwner)?.color ?? '#000'
  }, [message, applicationDealTeams])

  const maybeMarkAsRead = () => {
    if (isMessageFromCurrentUser(message) || isMessageSeenByCurrentUser(message)) return
    markStreamingNoteAsRead(message.uuid).then(() => {
      setSeen(true)
      setTimeout(() => setSeen(false), 2000)
    })
  }

  const selfReaction = message.reactions?.find((r) => r.user.id === loggedUser.id)

  const toggleReaction = async () => {
    if (selfReaction) {
      await deleteStreamingNoteReaction(selfReaction.uuid)
    } else {
      await reactToStreamingNote({ streaming_note_id: message.id })
    }
    refetch()
  }

  const isEdited = message.text_edited_at

  const extractUAMDeclineReason = (text: string) => {
    const match = text.match(/UAM Decline Reason: <strong>(.+?)<\/strong><\/li>/)
    if (match) {
      const reasons = match[1].split(', ')
      return reasons || null
    }
    return null
  }

  const extractLossReason = (text: string) => {
    const match = text.match(/Loss Reason: (.+?)<\/li>/)
    if (match) {
      const reasons = match[1].split(', ')
      return reasons[0] || null
    }
    return null
  }

  const handleOpenClosedLost = () => {
    const uamReasons = extractUAMDeclineReason(message.text)

    const firstReason = uamReasons?.length ? uamReasons[0] : null

    const lossReason = extractLossReason(message.text)

    let subLossReasons = []

    if (lossReason && firstReason) {
      subLossReasons = uamReasons?.map((uamReason) =>
        subLossReasonsOptions[lossReason].find((subReason) => subReason?.label === uamReason)
      )
      dispatch(setClosedLossDrawerSubReasons(subLossReasons))
    } else if (firstReason) {
      dispatch(setClosedLossDrawerSubReasonOtherText(uamReasons[0]))
    }

    dispatch(setClosedLossDrawerType('denied'))
    dispatch(setClosedLossDrawerReason(lossReason ? lossReason : 'other'))
    dispatch(setClosedLossDrawerOpen(true))

    if (lossReason === 'credit') {
      const aanReasons = []

      const subLossReasonsValues = subLossReasons?.map((subReason) => subReason?.value)

      if (subLossReasonsValues?.includes('bankruptcy')) aanReasons.push('bankruptcy')
      if (subLossReasonsValues?.includes('fico-less-than-500'))
        aanReasons.push('fico-less-than-500')
      if (subLossReasonsValues?.includes('mtg-dq-90-days')) aanReasons.push('delinquent-past')
      if (subLossReasonsValues?.includes('mtg-dq-120-days')) aanReasons.push('delinquent-past')
      if (subLossReasonsValues?.includes('mtg-dq-150-plus-days')) aanReasons.push('delinquent-past')
      if (subLossReasonsValues?.includes('mtg-collection-or-charge-off'))
        aanReasons.push('collection-action-or-judgment')
      if (subLossReasonsValues?.includes('mtg-repo-or-fc'))
        aanReasons.push('foreclosure-or-repossession')
      if (subLossReasonsValues?.includes('dti-over-45-percent'))
        aanReasons.push('excessive-obligations')
      if (subLossReasonsValues?.includes('dscr-below-1-25x'))
        aanReasons.push('excessive-obligations')
      if (subLossReasonsValues?.includes('garnishment-or-attachment'))
        aanReasons.push('garnishment-or-attachment')
      if (subLossReasonsValues?.includes('limited-credit-experience'))
        aanReasons.push('limited-credit-experience')
      if (subLossReasonsValues?.includes('no-credit-file')) aanReasons.push('no-credit-file')
      if (subLossReasonsValues?.includes('number-of-recent-inquiries-on-credit-bureau-report'))
        aanReasons.push('inquiries-credit-bureau-report')
      if (subLossReasonsValues?.includes('value-or-type-of-collateral-not-sufficient'))
        aanReasons.push('collateral-not-sufficient')

      dispatch(setAanReasons(aanReasons))
    }
  }

  return (
    <>
      <div
        className={`w-full card bg-base-100 shadow-md flex flex-col border ${
          isLastIndex && isReply ? 'rounded-b-2xl rounded-t-none' : 'rounded-none'
        } ${hasReplies && isRepliesOpen ? '!rounded-2xl !rounded-br-none' : !isReply && '!rounded-2xl'} ${isReply && 'bg-gray-100'}`}
        data-test='repliesPlaceHolderArray'
        onMouseEnter={() => {
          maybeMarkAsRead()
        }}
      >
        <div className={`flex justify-between px-4 py-3 rounded-t-box`}>
          <div className='flex flex-row gap-2 items-center'>
            <div>
              <Avatar
                user={message?.user?.user}
                color={avatarColor}
              />
            </div>
            <p
              className='text-sm card-title'
              data-test='displayUserName'
            >
              {displayUserName}
            </p>
          </div>
          <div
            className='flex flex-row gap-2'
            onClick={() => setShowFullDate(!showFullDate)}
          >
            <p className='flex items-center text-[10px] font-light text-right'>
              {showFullDate ? (
                <>{new Date(message.created_at).toLocaleString()}</>
              ) : (
                <>{formatDistanceToNow(new Date(message.created_at), { addSuffix: true })}</>
              )}
            </p>
          </div>
        </div>
        <div className='flex flex-col w-full px-4 pt-0 py-3 gap-2'>
          <div className='flex gap-1'>
            {isNoteDeleted(message) && (
              <div className='mt-[2px]'>
                <TrashIcon
                  width={18}
                  height={18}
                  className='flex flex-nowrap'
                />
              </div>
            )}
            <div
              className='break-words prose prose-sm prose-p:m-0 prose-a:bg-slate-200 prose-a:border prose-a:p-1 prose-a:rounded prose-a:no-underline'
              data-test='streaming-notes-text'
              dangerouslySetInnerHTML={{ __html: processMentionedEntity(message.text) }}
            />
          </div>

          {message?.note_type === 'uam-decline' && assignedHEO && (
            <p className='break-words prose prose-sm prose-p:m-0 prose-a:bg-slate-200 prose-a:border prose-a:p-1 prose-a:rounded prose-a:no-underline'>
              <a>{assignedHEO?.full_name}</a>
              &nbsp;
              <span
                className={isAssignedHEO ? 'link link-info text-left cursor-pointer' : ''}
                onClick={() => (isAssignedHEO ? handleOpenClosedLost() : null)}
              >
                click here to disposition this file to Closed Lost
              </span>
            </p>
          )}

          {/* Edit timestamp */}
          {isEdited && (
            <div className='flex flex-grow text-xs text-gray-400 justify-end'>
              Edited: {formatDistanceToNow(new Date(message.text_edited_at), { addSuffix: true })}
            </div>
          )}

          {message?.documents?.length > 0 && (
            <div className='flex flex-wrap gap-3'>
              {message?.documents?.map((document) => (
                <div key={document.id}>
                  <DownloadFile document={document} />
                </div>
              ))}
            </div>
          )}

          <div className='flex items-baseline justify-between'>
            <div className='flex flex-row gap-3 items-center'>
              {setIsRepliesOpen && hasReplies && (
                <div
                  className='text-xs link text-blue-600 hover:text-blue-800'
                  onClick={() => setIsRepliesOpen((current) => !current)}
                >
                  {isRepliesOpen ? 'Hide Replies' : 'Show Replies'}
                </div>
              )}
              {/* Likes */}
              <Popover className='relative'>
                <Popover.Button
                  className='text-gray-400 hover:text-gray-500 flex gap-1 items-center'
                  onDoubleClick={toggleReaction}
                  disabled={isDeletingSNReaction || isLoadingSNReaction}
                >
                  {selfReaction ? (
                    <HandThumbUpIconSolid
                      height={18}
                      width={18}
                      className='text-blue-500'
                    />
                  ) : (
                    <HandThumbUpIcon
                      height={18}
                      width={18}
                    />
                  )}
                  {message.reactions?.length ?? 0}
                </Popover.Button>
                <Popover.Panel className='absolute z-30 min-w-max bottom-6 left-0 max-w-sm overflow-hidden bg-white rounded shadow-lg border'>
                  <div className='p-3'>
                    <p className='text-sm text-gray-500'>
                      {message.reactions?.length > 0 ? (
                        <span>Liked by:</span>
                      ) : (
                        <span>No one has liked this note yet</span>
                      )}
                    </p>
                    {message.reactions?.length > 0 && (
                      <ul className='mt-3 flex gap-1 flex-wrap'>
                        {message.reactions.map((reaction) => (
                          <li
                            key={reaction.user.id}
                            className='items-center gap-2 text-sm text-gray-600'
                          >
                            <div>
                              {reaction.user.first_name} {reaction.user.last_name}
                            </div>
                            <small className='text-xs text-gray-400'>
                              {formatDistanceToNow(new Date(reaction.created_at), {
                                addSuffix: true,
                              })}
                            </small>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </Popover.Panel>
              </Popover>
              {/* Seens */}
              <Popover className='relative'>
                <Popover.Button className='text-gray-400 hover:text-gray-500 flex gap-1 items-center'>
                  {!seen ? (
                    <EyeIcon
                      height={18}
                      width={18}
                    />
                  ) : (
                    <div className='flex'>
                      <span className='text-xs text-green-500'>seen</span>
                      <CheckBadgeIcon
                        height={18}
                        width={18}
                        className='text-green-400'
                      />
                    </div>
                  )}

                  {message.seen_by.length}
                </Popover.Button>
                <Popover.Panel className='absolute z-30 bottom-6 left-0 w-48 max-w-sm overflow-hidden bg-white rounded shadow-lg border'>
                  <div className='p-3'>
                    <p className='text-sm text-gray-500'>
                      {message.seen_by.length > 0 ? (
                        <span>Seen by:</span>
                      ) : (
                        <span>No one has seen this note yet</span>
                      )}
                    </p>
                    {message.seen_by.length > 0 && (
                      <ul className='mt-1 flex gap-1 flex-wrap'>
                        {message.seen_by.map((userId) => (
                          <li
                            key={userId}
                            className='flex items-center gap-2 text-sm text-gray-500'
                          >
                            <Avatar user={getUserById(userId)} />
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </Popover.Panel>
              </Popover>
              <button
                type='button'
                className='text-blue-700 rounded hover:bg-blue-800 hover:text-white cursor-pointer disabled:text-gray-200 disabled:hover:bg-transparent disabled:cursor-not-allowed'
                onClick={() => setIsEditNoteOpen(true)}
                data-test='reply-button'
                disabled={!isMessageFromCurrentUser(message) || isNoteDeleted(message)}
              >
                <PencilSquareIcon height={18} />
              </button>

              <button
                type='button'
                className='text-red-600 rounded hover:bg-red-500 hover:text-white cursor-pointer disabled:text-gray-200 disabled:hover:bg-transparent disabled:cursor-not-allowed'
                onClick={() => setIsConfirmDeleteOpen(true)}
                data-test='delete-button'
                disabled={
                  !isMessageFromCurrentUser(message) ||
                  !isNoteDeletable(message) ||
                  isNoteDeleted(message)
                }
                data-for='deleteIcon'
                data-tip
              >
                <TrashIcon
                  width={18}
                  className='w-full'
                />
              </button>
            </div>

            <div>
              <div
                className='btn btn-sm btn-outline btn-primary'
                onClick={() => {
                  setShowComposer(true)
                  showComposer()
                }}
              >
                Reply
              </div>
            </div>
          </div>
        </div>

        <ConfirmDeleteModal
          isOpen={isConfirmDeleteOpen}
          isReply={isReply}
          setIsOpen={setIsConfirmDeleteOpen}
          message={message.text}
          onMessageDelete={async () => {
            await updateStreamingNote({
              uuid: message.uuid,
              text: DELETED_MESSAGE_TEXT,
            })
            setIsConfirmDeleteOpen(false)
          }}
        />

        <EditNoteModal
          isOpen={isEditNoteOpen}
          setIsOpen={setIsEditNoteOpen}
          message={message}
          updateStreamingNote={updateStreamingNote}
        />
      </div>
    </>
  )
}
